// src/pages/provider/ProviderProfilePage.jsx

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  User,
  Star,
  Briefcase,
  Clock,
  MapPin,
  Phone,
  Mail,
  Globe,
  Languages,
  Award,
  Camera,
  CheckCircle,
  XCircle,
  Edit,
  Save,
  X,
  Check,
  Upload,
  Trash2,
  Image as ImageIcon,
  Loader2
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import useTranslation from '../../hooks/useTranslation';
import colors from '../../constants/colors';

// Redux imports
import {
  selectCurrentProvider,
  selectProviderLoading,
  selectProviderUpdating,
  selectProviderUpdateSuccess,
  selectProviderUpdateError,
  selectProviderError,
  updateProvider,
  clearCurrentProvider,
  resetUpdateState,
  clearErrors,
} from '../../store/slices/providerSlice';
import {
  getProfileCompletion,
  selectProfileCompletion,
  selectProfile
} from '../../store/slices/profileSlice';
import {
  uploadProfileImage,
  uploadCoverImage,
  selectProfileImage,
  selectCoverImage,
  selectImageUploading,
  selectUploadProgress,
  selectUploadSuccess,
  selectUploadError,
  resetUploadState,
  clearImageErrors,
} from '../../store/slices/providerImageSlice';
import {
  getReviewStatistics,
  selectReviewStatistics,
  selectReviewsLoading
} from '../../store/slices/serviceProviderReviewSlice';
import { 
  selectUser, 
  selectIsAuthenticated, 
  updateUser 
} from '../../store/slices/authSlice';

import styles from '../../styles/ProviderProfilePage.module.css';

const ProviderProfilePage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Add refs to prevent infinite loops
  const initialLoadDone = useRef(false);
  const isMounted = useRef(true);
  const dataPopulatedRef = useRef(false);
  const previousProfileDataRef = useRef(null);
  
  // Ref to track if profile completion has been loaded
  const completionLoadedRef = useRef(false);
  
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  
  // Image states
  const [selectedProfileImage, setSelectedProfileImage] = useState(null);
  const [selectedCoverImage, setSelectedCoverImage] = useState(null);
  const [localProfileImage, setLocalProfileImage] = useState(null);
  const [localCoverImage, setLocalCoverImage] = useState(null);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [uploadingImageType, setUploadingImageType] = useState(null);
  const [uploadAttempts, setUploadAttempts] = useState(0);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isSavingImages, setIsSavingImages] = useState(false);
  
  // Track pending image changes
  const [pendingProfileImage, setPendingProfileImage] = useState(null);
  const [pendingCoverImage, setPendingCoverImage] = useState(null);
  const [hasImageChanges, setHasImageChanges] = useState(false);

  // Separate state for text inputs
  const [languagesText, setLanguagesText] = useState('');

  // Local form state
  const [localFormData, setLocalFormData] = useState({
    businessName: '',
    description: '',
    serviceType: '',
    yearsOfExperience: '',
    phone: '',
    email: '',
    website: '',
    address: {
      street: '',
      city: '',
      state: '',
      country: '',
      zipCode: ''
    },
    languages: [],
    availability: {},
  });

  // Redux selectors
  const authUser = useSelector(selectUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const provider = useSelector(selectCurrentProvider);
  const profile = useSelector(selectProfile);
  const isLoading = useSelector(selectProviderLoading);
  const isUpdating = useSelector(selectProviderUpdating);
  const updateSuccess = useSelector(selectProviderUpdateSuccess);
  const updateError = useSelector(selectProviderUpdateError);
  const error = useSelector(selectProviderError);
  const profileCompletion = useSelector(selectProfileCompletion);
  const reviewStats = useSelector(selectReviewStatistics);
  const reviewsLoading = useSelector(selectReviewsLoading);

  const profileImage = useSelector(selectProfileImage);
  const coverImage = useSelector(selectCoverImage);
  const isUploading = useSelector(selectImageUploading);
  const uploadProgress = useSelector(selectUploadProgress);
  const uploadSuccess = useSelector(selectUploadSuccess);
  const uploadError = useSelector(selectUploadError);

  const { currentLanguage } = useLanguage();

  // ======================================================
  // TRANSLATIONS
  // ======================================================
  const { translatedText: providerProfile } = useTranslation('Provider Profile');
  const { translatedText: loadingProfile } = useTranslation('Loading profile...');
  const { translatedText: editProfile } = useTranslation('Edit Profile');
  const { translatedText: saveChanges } = useTranslation('Save Changes');
  const { translatedText: cancel } = useTranslation('Cancel');
  const { translatedText: profileUpdated } = useTranslation('Profile updated successfully');
  const { translatedText: success } = useTranslation('Success');
  const { translatedText: errorTitle } = useTranslation('Error');
  const { translatedText: businessNameRequired } = useTranslation('Business name is required');
  const { translatedText: categoryRequired } = useTranslation('Category is required');
  const { translatedText: imageUpdated } = useTranslation('Image updated successfully');
  const { translatedText: coverImageUpdated } = useTranslation('Cover image updated successfully');
  const { translatedText: imageRemoved } = useTranslation('Image removed successfully');
  const { translatedText: coverImageRemoved } = useTranslation('Cover image removed successfully');
  const { translatedText: tryAgain } = useTranslation('Please try again');
  const { translatedText: removePhoto } = useTranslation('Remove Photo');
  const { translatedText: removeCover } = useTranslation('Remove Cover');
  const { translatedText: uploading } = useTranslation('Uploading...');
  const { translatedText: deleteConfirm } = useTranslation('Are you sure you want to remove this image?');
  const { translatedText: deleteCoverConfirm } = useTranslation('Are you sure you want to remove this cover image?');
  const { translatedText: changePhoto } = useTranslation('Change Photo');
  const { translatedText: changeCover } = useTranslation('Change Cover');
  const { translatedText: uploadCoverPhoto } = useTranslation('Upload Cover Photo');
  
  const { translatedText: businessNameLabel } = useTranslation('Business Name');
  const { translatedText: descriptionLabel } = useTranslation('Description');
  const { translatedText: categoryLabel } = useTranslation('Category');
  const { translatedText: yearsExperienceLabel } = useTranslation('Years of Experience');
  const { translatedText: addressLabel } = useTranslation('Address');
  const { translatedText: streetLabel } = useTranslation('Street');
  const { translatedText: cityLabel } = useTranslation('City');
  const { translatedText: stateLabel } = useTranslation('State');
  const { translatedText: countryLabel } = useTranslation('Country');
  const { translatedText: phoneLabel } = useTranslation('Phone');
  const { translatedText: emailLabel } = useTranslation('Email');
  const { translatedText: websiteLabel } = useTranslation('Website');
  const { translatedText: languagesLabel } = useTranslation('Languages');
  const { translatedText: availableForWork } = useTranslation('Available for work');
  const { translatedText: currentlyUnavailable } = useTranslation('Currently unavailable');
  const { translatedText: noLanguages } = useTranslation('No languages specified');
  const { translatedText: noCertifications } = useTranslation('No certifications');
  const { translatedText: jobsCompleted } = useTranslation('Jobs Completed');
  const { translatedText: avgResponse } = useTranslation('Avg Response');
  const { translatedText: ratingLabel } = useTranslation('Rating');
  const { translatedText: haitianServices } = useTranslation('Haitian Services Marketplace');
  const { translatedText: connectingHaitians } = useTranslation('Connecting Haitians Worldwide, One Service at a Time');
  const { translatedText: madeWithLove } = useTranslation('Made with ❤️ in Haiti');
  const { translatedText: notProvided } = useTranslation('Not provided');
  const { translatedText: enterStreet } = useTranslation('Enter street address');
  const { translatedText: enterCity } = useTranslation('Enter city');
  const { translatedText: enterState } = useTranslation('Enter state');
  const { translatedText: enterCountry } = useTranslation('Enter country');
  const { translatedText: enterPhone } = useTranslation('Enter phone number');
  const { translatedText: enterEmail } = useTranslation('Enter email address');
  const { translatedText: enterWebsite } = useTranslation('Enter website URL');
  const { translatedText: enterLanguages } = useTranslation('Enter languages (comma separated)');
  const { translatedText: profileCompletionLabel } = useTranslation('Profile Completion');
  const { translatedText: completionPercentage } = useTranslation('Complete your profile');

  // ======================================================
  // FOOTER TRANSLATIONS
  // ======================================================
  const { translatedText: homeTitle } = useTranslation('Haitian Services');
  const { translatedText: quickLinks } = useTranslation('Quick Links');
  const { translatedText: findProviders } = useTranslation('Find Providers');
  const { translatedText: supportUs } = useTranslation('Support Us');
  const { translatedText: helpAndSupport } = useTranslation('Help & Support');
  const { translatedText: aboutUs } = useTranslation('About Us');
  const { translatedText: forProviders } = useTranslation('For Providers');
  const { translatedText: becomeAProvider } = useTranslation('Become a Provider');
  const { translatedText: providerLogin } = useTranslation('Provider Login');
  const { translatedText: dashboardLabel } = useTranslation('Dashboard');
  const { translatedText: settings } = useTranslation('Settings');
  const { translatedText: legal } = useTranslation('Legal');
  const { translatedText: termsOfService } = useTranslation('Terms of Service');
  const { translatedText: privacyPolicy } = useTranslation('Privacy Policy');
  const { translatedText: cookiePolicy } = useTranslation('Cookie Policy');

  // ======================================================
  // GET PROVIDER ID - Memoized
  // ======================================================
  const getProviderId = useCallback(() => {
    return profile?.providerProfile?._id || authUser?.providerProfile?._id || null;
  }, [profile, authUser]);

  // ======================================================
  // IS AVAILABLE - Memoized
  // ======================================================
  const isAvailableToday = useCallback(() => {
    const providerData = profile?.providerProfile || authUser?.providerProfile;
    if (!providerData?.availability) return false;
    
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const today = days[new Date().getDay()];
    const todaySchedule = providerData.availability[today];
    
    return todaySchedule?.available === true;
  }, [profile, authUser]);

  // ======================================================
  // GET TODAY'S SCHEDULE - Memoized
  // ======================================================
  const getTodaySchedule = useCallback(() => {
    const providerData = profile?.providerProfile || authUser?.providerProfile;
    if (!providerData?.availability) return null;
    
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const today = days[new Date().getDay()];
    
    return providerData.availability[today] || null;
  }, [profile, authUser]);

  // ======================================================
  // POPULATE FORM DATA - Fixed with ref to prevent loops
  // ======================================================
  const populateFormData = useCallback((providerData) => {
    if (!providerData) return;
    
    const isSameData = previousProfileDataRef.current && 
      previousProfileDataRef.current.businessName === providerData.businessName &&
      previousProfileDataRef.current.description === providerData.description &&
      previousProfileDataRef.current.serviceType === providerData.serviceType;
    
    if (isSameData && dataPopulatedRef.current) {
      return;
    }
    
    setLocalFormData({
      businessName: providerData.businessName || '',
      description: providerData.description || '',
      serviceType: providerData.serviceType || '',
      yearsOfExperience: providerData.yearsOfExperience?.toString() || '',
      phone: providerData.phone || '',
      email: providerData.email || '',
      website: providerData.website || '',
      address: {
        street: providerData.address?.street || '',
        city: providerData.address?.city || '',
        state: providerData.address?.state || '',
        country: providerData.address?.country || '',
        zipCode: providerData.address?.zipCode || ''
      },
      languages: providerData.languages || [],
      availability: providerData.availability || {},
    });

    setLanguagesText((providerData.languages || []).join(', '));
    
    dataPopulatedRef.current = true;
    previousProfileDataRef.current = {
      businessName: providerData.businessName,
      description: providerData.description,
      serviceType: providerData.serviceType,
    };
  }, []);

  // ======================================================
  // LOAD PROFILE COMPLETION - Fixed with ref guard
  // ======================================================
  const loadProfileCompletion = useCallback(async () => {
    if (completionLoadedRef.current) {
      return;
    }
    
    try {
      completionLoadedRef.current = true;
      await dispatch(getProfileCompletion()).unwrap();
    } catch (error) {
      console.error('Failed to load profile completion:', error);
      completionLoadedRef.current = false;
    }
  }, [dispatch]);

  // ======================================================
  // HANDLE EDIT
  // ======================================================
  const handleEdit = () => {
    const providerData = profile?.providerProfile || authUser?.providerProfile;

    if (!providerData) {
      alert(`${errorTitle}\n\nProvider profile data not found.`);
      return;
    }

    populateFormData(providerData);
    setIsEditing(true);
    setSelectedProfileImage(null);
    setSelectedCoverImage(null);
    // Reset pending changes
    setPendingProfileImage(null);
    setPendingCoverImage(null);
    setHasImageChanges(false);
  };

  // ======================================================
  // HANDLE CANCEL
  // ======================================================
  const handleCancel = () => {
    const providerData = profile?.providerProfile || authUser?.providerProfile;

    if (providerData) {
      populateFormData(providerData);
    }

    setIsEditing(false);
    setSelectedProfileImage(null);
    setSelectedCoverImage(null);
    // Reset images to original
    const profileImgUrl = providerData?.profileImage?.url || null;
    const coverImgUrl = providerData?.coverImage?.url || null;
    setLocalProfileImage(profileImgUrl);
    setLocalCoverImage(coverImgUrl);
    // Reset pending changes
    setPendingProfileImage(null);
    setPendingCoverImage(null);
    setHasImageChanges(false);
  };

  // ======================================================
  // UPDATE FORM DATA WHEN PROFILE CHANGES
  // ======================================================
  useEffect(() => {
    const providerData = profile?.providerProfile || authUser?.providerProfile;
    
    if (providerData && !isEditing && !dataPopulatedRef.current) {
      populateFormData(providerData);
    }
    
    // Set images from profile
    if (providerData && !isEditing) {
      setLocalProfileImage(providerData.profileImage?.url || null);
      setLocalCoverImage(providerData.coverImage?.url || null);
    }
    
    if (providerData && previousProfileDataRef.current) {
      const hasChanged = 
        previousProfileDataRef.current.businessName !== providerData.businessName ||
        previousProfileDataRef.current.description !== providerData.description ||
        previousProfileDataRef.current.serviceType !== providerData.serviceType;
      
      if (hasChanged) {
        dataPopulatedRef.current = false;
        populateFormData(providerData);
      }
    }
  }, [profile, authUser, isEditing, populateFormData]);

  // ======================================================
  // HANDLE UPDATE SUCCESS - Only for profile updates
  // ======================================================
  useEffect(() => {
    if (updateSuccess) {
      alert(`${success}\n\n${profileUpdated}`);
      dispatch(resetUpdateState());
      setIsEditing(false);
      dataPopulatedRef.current = false;
      previousProfileDataRef.current = null;
      completionLoadedRef.current = false;
      loadProfileCompletion();
      // Reset image states after successful save
      setPendingProfileImage(null);
      setPendingCoverImage(null);
      setHasImageChanges(false);
    }
  }, [updateSuccess, dispatch, loadProfileCompletion, profileUpdated, success]);

  // ======================================================
  // HANDLE ERRORS
  // ======================================================
  useEffect(() => {
    if (updateError || error) {
      alert(`${errorTitle}\n\n${updateError || error || tryAgain}`);
      dispatch(clearErrors());
    }
  }, [updateError, error, dispatch, errorTitle, tryAgain]);

  // ======================================================
  // HANDLE UPLOAD SUCCESS - Only for success notification
  // ======================================================
  useEffect(() => {
    if (uploadSuccess) {
      // Don't auto-save or auto-update - just mark as uploaded
      dispatch(resetUploadState());
      setIsImageUploading(false);
    }
  }, [uploadSuccess, dispatch]);

  // ======================================================
  // HANDLE UPLOAD ERROR
  // ======================================================
  useEffect(() => {
    if (uploadError) {
      alert(`${errorTitle}\n\n${uploadError}`);
      dispatch(clearImageErrors());
      setIsImageUploading(false);
      // Revert preview on error
      if (uploadingImageType === 'profile') {
        const originalUrl = authUser?.providerProfile?.profileImage?.url || null;
        setLocalProfileImage(originalUrl);
        setSelectedProfileImage(null);
        setPendingProfileImage(null);
      } else if (uploadingImageType === 'cover') {
        const originalUrl = authUser?.providerProfile?.coverImage?.url || null;
        setLocalCoverImage(originalUrl);
        setSelectedCoverImage(null);
        setPendingCoverImage(null);
      }
    }
  }, [uploadError, dispatch, errorTitle, authUser, uploadingImageType]);

  // ======================================================
  // CLEANUP ON UNMOUNT
  // ======================================================
  useEffect(() => {
    return () => {
      isMounted.current = false;
      dispatch(clearCurrentProvider());
      dispatch(resetUpdateState());
      dispatch(clearErrors());
      dispatch(resetUploadState());
      dispatch(clearImageErrors());
      completionLoadedRef.current = false;
      initialLoadDone.current = false;
      dataPopulatedRef.current = false;
    };
  }, [dispatch]);

  // ======================================================
  // HANDLE IMAGE PICK - Only uploads to server, doesn't auto-save
  // ======================================================
  const handleImagePick = (type) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        if (!file.type.startsWith('image/')) {
          alert('Please select an image file');
          return;
        }
        if (file.size > 5 * 1024 * 1024) {
          alert('Image size should be less than 5MB');
          return;
        }

        // Immediately show preview
        const reader = new FileReader();
        reader.onload = (event) => {
          if (type === 'profile') {
            setSelectedProfileImage(event.target.result);
            setLocalProfileImage(event.target.result);
            setPendingProfileImage(event.target.result);
          } else {
            setSelectedCoverImage(event.target.result);
            setLocalCoverImage(event.target.result);
            setPendingCoverImage(event.target.result);
          }
          setHasImageChanges(true);
        };
        reader.readAsDataURL(file);
        
        const providerId = getProviderId();
        if (!providerId) {
          alert(`${errorTitle}\n\nProvider ID not found`);
          return;
        }

        setUploadingImageType(type);
        setIsImageUploading(true);
        setUploadAttempts(prev => prev + 1);
        
        try {
          let result;
          if (type === 'profile') {
            result = await dispatch(uploadProfileImage({ 
              providerId, 
              file,
              options: { width: 800, height: 800 }
            })).unwrap();
          } else {
            result = await dispatch(uploadCoverImage({ 
              providerId, 
              file,
              options: { width: 1200, height: 400 }
            })).unwrap();
          }
          
          const imageData = result?.data || result;
          if (imageData?.url) {
            const imageUrl = imageData.url;
            
            // Store the uploaded image URL in pending state
            if (type === 'profile') {
              setPendingProfileImage(imageUrl);
              setLocalProfileImage(imageUrl);
              setSelectedProfileImage(null);
            } else {
              setPendingCoverImage(imageUrl);
              setLocalCoverImage(imageUrl);
              setSelectedCoverImage(null);
            }
            
            setHasImageChanges(true);
          }
          
          setUploadAttempts(0);
          setIsImageUploading(false);
        } catch (err) {
          console.error('Upload error:', err);
          setIsImageUploading(false);
          // Revert preview on error
          if (type === 'profile') {
            const originalUrl = authUser?.providerProfile?.profileImage?.url || null;
            setLocalProfileImage(originalUrl);
            setSelectedProfileImage(null);
            setPendingProfileImage(null);
          } else {
            const originalUrl = authUser?.providerProfile?.coverImage?.url || null;
            setLocalCoverImage(originalUrl);
            setSelectedCoverImage(null);
            setPendingCoverImage(null);
          }
        }
      }
    };
    input.click();
  };

  // ======================================================
  // HANDLE IMAGE REMOVE - Only marks for removal, doesn't auto-save
  // ======================================================
  const handleRemoveImage = (type) => {
    const confirmMessage = type === 'cover' ? deleteCoverConfirm : deleteConfirm;
    if (window.confirm(confirmMessage)) {
      // Update local state (preview)
      if (type === 'profile') {
        setSelectedProfileImage(null);
        setLocalProfileImage(null);
        setPendingProfileImage(null); // null means remove
      } else {
        setSelectedCoverImage(null);
        setLocalCoverImage(null);
        setPendingCoverImage(null); // null means remove
      }
      setHasImageChanges(true);
    }
  };

  // ======================================================
  // HANDLE SAVE IMAGES - Saves all pending image changes
  // ======================================================
  const handleSaveImages = async () => {
    setIsSavingImages(true);
    
    try {
      const currentProviderData = authUser?.providerProfile || {};
      
      // Prepare update data with pending images
      const updateData = {
        providerProfile: {
          ...currentProviderData,
          ...(pendingProfileImage !== undefined && {
            profileImage: pendingProfileImage ? { url: pendingProfileImage } : null
          }),
          ...(pendingCoverImage !== undefined && {
            coverImage: pendingCoverImage ? { url: pendingCoverImage } : null
          })
        }
      };
      
      // Update Redux auth state
      dispatch(updateUser(updateData));
      
      // Update localStorage
      try {
        const userDataStr = localStorage.getItem('user_data');
        if (userDataStr) {
          const userData = JSON.parse(userDataStr);
          const updatedUserData = {
            ...userData,
            providerProfile: {
              ...(userData.providerProfile || {}),
              ...(pendingProfileImage !== undefined && {
                profileImage: pendingProfileImage ? { url: pendingProfileImage } : null
              }),
              ...(pendingCoverImage !== undefined && {
                coverImage: pendingCoverImage ? { url: pendingCoverImage } : null
              })
            }
          };
          localStorage.setItem('user_data', JSON.stringify(updatedUserData));
          console.log('✅ Images saved to localStorage');
        }
      } catch (storageError) {
        console.error('❌ Failed to save images to localStorage:', storageError);
      }
      
      alert(`${success}\n\nImages saved successfully!`);
      
      // Reset pending changes after successful save
      setPendingProfileImage(null);
      setPendingCoverImage(null);
      setHasImageChanges(false);
      
    } catch (error) {
      console.error('Error saving images:', error);
      alert(`${errorTitle}\n\nFailed to save images. Please try again.`);
    } finally {
      setIsSavingImages(false);
    }
  };

  // ======================================================
  // HANDLE SAVE PROFILE
  // ======================================================
  const handleSave = () => {
    if (!localFormData.businessName?.trim()) {
      alert(`${errorTitle}\n\n${businessNameRequired}`);
      return;
    }
    if (!localFormData.serviceType?.trim()) {
      alert(`${errorTitle}\n\n${categoryRequired}`);
      return;
    }

    const providerId = getProviderId();
    if (!providerId) {
      alert(`${errorTitle}\n\nProvider ID not found`);
      return;
    }

    const languagesArray = languagesText
      .split(',')
      .map(s => s.trim())
      .filter(s => s);

    const updateData = {
      businessName: localFormData.businessName,
      description: localFormData.description,
      serviceType: localFormData.serviceType,
      yearsOfExperience: parseInt(localFormData.yearsOfExperience) || 0,
      phone: localFormData.phone,
      email: localFormData.email,
      website: localFormData.website,
      address: localFormData.address,
      languages: languagesArray,
    };

    dispatch(updateProvider({ id: providerId, data: updateData }));
  };

  // ======================================================
  // GET IMAGE URI
  // ======================================================
  const getProfileImageUri = () => {
    if (localProfileImage) {
      return localProfileImage;
    }
    if (selectedProfileImage) {
      return selectedProfileImage;
    }
    if (profileImage?.url) {
      return profileImage.url;
    }
    const providerData = authUser?.providerProfile;
    if (providerData?.profileImage?.url) {
      return providerData.profileImage.url;
    }
    const businessName = localFormData.businessName || 'Provider';
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(businessName)}&background=3498db&color=fff&size=200&rounded=true`;
  };

  const getCoverImageUri = () => {
    if (localCoverImage) {
      return localCoverImage;
    }
    if (selectedCoverImage) {
      return selectedCoverImage;
    }
    if (coverImage?.url) {
      return coverImage.url;
    }
    const providerData = authUser?.providerProfile;
    if (providerData?.coverImage?.url) {
      return providerData.coverImage.url;
    }
    return null;
  };

  // ======================================================
  // GETTER FUNCTIONS
  // ======================================================
  const getBusinessName = () => {
    return profile?.providerProfile?.businessName || authUser?.providerProfile?.businessName || localFormData.businessName || 'Provider';
  };

  const getCategory = () => {
    return profile?.providerProfile?.serviceType || authUser?.providerProfile?.serviceType || localFormData.serviceType || '';
  };

  const getRating = () => {
    return provider?.rating || profile?.providerProfile?.rating || authUser?.providerProfile?.rating || 0;
  };

  const getTotalReviews = () => {
    return reviewStats?.totalReviews || provider?.totalReviews || profile?.providerProfile?.totalReviews || authUser?.providerProfile?.totalReviews || 0;
  };

  const getTotalJobsCompleted = () => {
    return provider?.totalJobsCompleted || profile?.providerProfile?.totalJobsCompleted || authUser?.providerProfile?.totalJobsCompleted || 0;
  };

  const getResponseTime = () => {
    return provider?.responseTime || profile?.providerProfile?.responseTime || authUser?.providerProfile?.responseTime || 0;
  };

  // ======================================================
  // RENDER FIELD
  // ======================================================
  const renderField = (label, value, key, multiline = false) => {
    if (isEditing) {
      let editValue = localFormData[key];
      
      if (Array.isArray(editValue)) {
        if (key === 'languages') {
          editValue = languagesText;
        } else {
          editValue = editValue.join(', ');
        }
      } else if (typeof editValue === 'object' && editValue !== null) {
        editValue = '';
      } else {
        editValue = editValue || '';
      }

      return (
        <div className={styles.fieldContainer} key={`edit-${key}`}>
          <label className={styles.fieldLabel}>{label}</label>
          {multiline ? (
            <textarea
              className={`${styles.fieldInput} ${styles.textarea}`}
              value={String(editValue)}
              onChange={(e) => {
                if (Array.isArray(localFormData[key])) {
                  if (key === 'languages') {
                    setLanguagesText(e.target.value);
                  }
                } else if (typeof localFormData[key] === 'object' && localFormData[key] !== null && !Array.isArray(localFormData[key])) {
                  return;
                } else {
                  setLocalFormData({
                    ...localFormData,
                    [key]: e.target.value
                  });
                }
              }}
              placeholder={`Enter ${label.toLowerCase()}`}
              rows={4}
            />
          ) : (
            <input
              type="text"
              className={styles.fieldInput}
              value={String(editValue)}
              onChange={(e) => {
                if (Array.isArray(localFormData[key])) {
                  if (key === 'languages') {
                    setLanguagesText(e.target.value);
                  }
                } else if (typeof localFormData[key] === 'object' && localFormData[key] !== null && !Array.isArray(localFormData[key])) {
                  return;
                } else {
                  setLocalFormData({
                    ...localFormData,
                    [key]: e.target.value
                  });
                }
              }}
              placeholder={`Enter ${label.toLowerCase()}`}
            />
          )}
        </div>
      );
    }
    
    let displayValue = value;
    if (Array.isArray(value)) {
      displayValue = value.length > 0 ? value.join(', ') : notProvided;
    } else if (value === null || value === undefined || value === '') {
      displayValue = notProvided;
    } else if (typeof value === 'object') {
      if (value.street !== undefined || value.city !== undefined) {
        const parts = [];
        if (value.street) parts.push(value.street);
        if (value.city) parts.push(value.city);
        if (value.state) parts.push(value.state);
        if (value.country) parts.push(value.country);
        displayValue = parts.length > 0 ? parts.join(', ') : notProvided;
      } else {
        displayValue = JSON.stringify(value);
      }
    }
    
    return (
      <div className={styles.fieldContainer} key={`view-${key}`}>
        <label className={styles.fieldLabel}>{label}</label>
        <div className={styles.fieldValue}>{displayValue}</div>
      </div>
    );
  };

  // ======================================================
  // LOADING STATE
  // ======================================================
  if (isInitialLoad && isLoading) {
    return (
      <div className={styles.providerProfileContainer}>
        <div className={styles.profileHeaderBar}>
          <h2>{providerProfile}</h2>
        </div>
        <div className={styles.loadingContainer}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className={styles.loadingText}>{loadingProfile}</p>
        </div>
      </div>
    );
  }

  const providerData = profile?.providerProfile || authUser?.providerProfile;
  const isAvailable = isAvailableToday();
  const todaySchedule = getTodaySchedule();
  const hasCoverImage = getCoverImageUri() !== null;

  // ======================================================
  // MAIN RENDER
  // ======================================================
  return (
    <div className={styles.providerProfileContainer}>
      <div className={styles.profileHeaderBar}>
        <h2>{providerProfile}</h2>
        {!isEditing && (
          <button
            type="button"
            className={styles.editProfileBtn}
            onClick={handleEdit}
          >
            <Edit size={18} />
            {editProfile}
          </button>
        )}
      </div>

      <div className={styles.profileContent}>
        {/* Cover Image */}
        <div className={styles.coverImageContainer}>
          {hasCoverImage ? (
            <img
              src={getCoverImageUri()}
              alt="Cover"
              className={styles.coverImage}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          ) : (
            <div className={styles.coverImagePlaceholder}>
              <ImageIcon size={48} color="rgba(255,255,255,0.7)" />
              <span className={styles.coverPlaceholderText}>Add Cover Image</span>
            </div>
          )}
          {isEditing && !isImageUploading && !isUploading && (
            <>
              <button 
                className={styles.editCoverOverlay}
                onClick={() => handleImagePick('cover')}
                title={hasCoverImage ? changeCover : uploadCoverPhoto}
              >
                <Camera size={20} color="white" />
              </button>
              {hasCoverImage && (
                <button 
                  className={styles.removeCoverOverlay}
                  onClick={() => handleRemoveImage('cover')}
                  title={removeCover}
                >
                  <Trash2 size={16} color="white" />
                </button>
              )}
            </>
          )}
          {(isImageUploading && uploadingImageType === 'cover') && (
            <div className={styles.uploadingOverlay}>
              <Loader2 size={30} className={styles.spinner} />
              <span className={styles.uploadingText}>
                {uploading} {uploadProgress}%
              </span>
            </div>
          )}
        </div>

        {/* Profile Header */}
        <div className={styles.profileHeader}>
          <div className={styles.profileImageContainer}>
            <img
              src={getProfileImageUri()}
              alt="Profile"
              className={styles.profileImage}
              onError={(e) => {
                if (!selectedProfileImage && !profileImage?.url) {
                  const businessName = getBusinessName();
                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(businessName)}&background=3498db&color=fff&size=200&rounded=true`;
                }
              }}
            />
            {isEditing && !isImageUploading && !isUploading && (
              <button 
                className={styles.editImageOverlay}
                onClick={() => handleImagePick('profile')}
                title={changePhoto}
              >
                <Camera size={20} color="white" />
              </button>
            )}
            {(isImageUploading && uploadingImageType === 'profile') && (
              <div className={styles.uploadingOverlay}>
                <Loader2 size={30} className={styles.spinner} />
                <span className={styles.uploadingText}>
                  {uploading} {uploadProgress}%
                </span>
              </div>
            )}
          </div>
          <h4 className={styles.profileName}>{getBusinessName()}</h4>
          <div className={styles.profileCategory}>{getCategory() || 'Service Provider'}</div>
          <div className={styles.ratingContainer}>
            <Star size={16} color={colors.accentGold} fill={colors.accentGold} />
            <span className={styles.ratingText}>{getRating().toFixed(1)}</span>
            <span className={styles.reviewCount}>({getTotalReviews()} reviews)</span>
          </div>
        </div>

        {/* Profile Completion */}
        {profileCompletion && profileCompletion.percentage < 100 && (
          <div className={styles.completionContainer}>
            <div className={styles.completionHeader}>
              <span className={styles.completionTitle}>{profileCompletionLabel}</span>
              <span 
                className={`${styles.completionPercentage} ${profileCompletion.percentage >= 80 ? styles.medium : ''}`}
                data-percentage={profileCompletion.percentage}
              >
                {profileCompletion.percentage}%
              </span>
            </div>
            <div className={styles.completionBarContainer}>
              <div 
                className={`${styles.completionBar} ${profileCompletion.percentage >= 80 ? styles.medium : ''}`}
                style={{ width: `${profileCompletion.percentage}%` }}
                data-percentage={profileCompletion.percentage}
              />
            </div>
            {profileCompletion.missingFields?.length > 0 && (
              <div className={`${styles.completionHint} ${styles.warning}`}>
                <span className={styles.completionHintLabel}>{completionPercentage}:</span>
                <span className={styles.completionHintFields}>
                  {profileCompletion.missingFields.map((field, index) => (
                    <span key={index}>{field}</span>
                  ))}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Availability Status */}
        <div className={styles.availabilityContainer}>
          <div className={`${styles.availabilityBadge} ${!isAvailable ? styles.unavailable : ''}`}>
            <span className={isAvailable ? styles.availabilityDot : styles.availabilityDotUnavailable} />
            <span className={isAvailable ? styles.availabilityText : styles.availabilityTextUnavailable}>
              {isAvailable ? availableForWork : currentlyUnavailable}
            </span>
            {todaySchedule && (
              <span className={styles.scheduleInfo}>
                ({todaySchedule.start} - {todaySchedule.end})
              </span>
            )}
          </div>
        </div>

        {/* Edit/Save Buttons */}
        {isEditing && (
          <div className={styles.actionButtonsContainer}>
            <button 
              className={styles.cancelBtn}
              onClick={handleCancel}
              disabled={isUpdating || isImageUploading || isUploading || isSavingImages}
            >
              {cancel}
            </button>
            {hasImageChanges && (
              <button 
                className={styles.saveBtn}
                onClick={handleSaveImages}
                disabled={isUpdating || isImageUploading || isUploading || isSavingImages}
              >
                {isSavingImages ? (
                  <>
                    <Loader2 size={18} className={styles.spinner} />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    Save Images
                  </>
                )}
              </button>
            )}
            <button 
              className={styles.saveBtn}
              onClick={handleSave}
              disabled={isUpdating || isImageUploading || isUploading || isSavingImages}
            >
              {isUpdating ? (
                <div className="spinner-border spinner-border-sm text-white" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              ) : (
                <>
                  <Save size={18} />
                  {saveChanges}
                </>
              )}
            </button>
          </div>
        )}

        {/* Business Information */}
        <div className={styles.section}>
          <h6 className={styles.sectionTitle}>{businessNameLabel}</h6>
          {renderField(
            businessNameLabel,
            isEditing ? localFormData.businessName : getBusinessName(),
            'businessName'
          )}
          {renderField(
            descriptionLabel,
            isEditing ? localFormData.description : providerData?.description,
            'description',
            true
          )}
          {renderField(
            categoryLabel,
            isEditing ? localFormData.serviceType : getCategory(),
            'serviceType'
          )}
          {renderField(
            yearsExperienceLabel,
            isEditing
              ? localFormData.yearsOfExperience
              : providerData?.yearsOfExperience,
            'yearsOfExperience'
          )}
        </div>

        {/* Address Section */}
        <div className={styles.section}>
          <h6 className={styles.sectionTitle}>{addressLabel}</h6>
          {isEditing ? (
            <>
              <div className={styles.fieldContainer}>
                <label className={styles.fieldLabel}>{streetLabel}</label>
                <input
                  type="text"
                  className={styles.fieldInput}
                  value={localFormData.address?.street || ''}
                  onChange={(e) => setLocalFormData({ 
                    ...localFormData, 
                    address: { ...localFormData.address, street: e.target.value } 
                  })}
                  placeholder={enterStreet}
                />
              </div>
              <div className={styles.fieldContainer}>
                <label className={styles.fieldLabel}>{cityLabel}</label>
                <input
                  type="text"
                  className={styles.fieldInput}
                  value={localFormData.address?.city || ''}
                  onChange={(e) => setLocalFormData({ 
                    ...localFormData, 
                    address: { ...localFormData.address, city: e.target.value } 
                  })}
                  placeholder={enterCity}
                />
              </div>
              <div className={styles.fieldContainer}>
                <label className={styles.fieldLabel}>{stateLabel}</label>
                <input
                  type="text"
                  className={styles.fieldInput}
                  value={localFormData.address?.state || ''}
                  onChange={(e) => setLocalFormData({ 
                    ...localFormData, 
                    address: { ...localFormData.address, state: e.target.value } 
                  })}
                  placeholder={enterState}
                />
              </div>
              <div className={styles.fieldContainer}>
                <label className={styles.fieldLabel}>{countryLabel}</label>
                <input
                  type="text"
                  className={styles.fieldInput}
                  value={localFormData.address?.country || ''}
                  onChange={(e) => setLocalFormData({ 
                    ...localFormData, 
                    address: { ...localFormData.address, country: e.target.value } 
                  })}
                  placeholder={enterCountry}
                />
              </div>
            </>
          ) : (
            <div className={styles.fieldContainer}>
              <label className={styles.fieldLabel}>{addressLabel}</label>
              <div className={styles.fieldValue}>
                {providerData?.address ? 
                  `${providerData.address.street || ''}${providerData.address.street ? ', ' : ''}${providerData.address.city || ''}${providerData.address.city ? ', ' : ''}${providerData.address.state || ''}${providerData.address.state ? ', ' : ''}${providerData.address.country || ''}` 
                  : notProvided}
              </div>
            </div>
          )}
        </div>

        {/* Contact Information */}
        <div className={styles.section}>
          <h6 className={styles.sectionTitle}>Contact</h6>
          {renderField(phoneLabel, providerData?.phone, 'phone')}
          {renderField(emailLabel, providerData?.email, 'email')}
          {renderField(websiteLabel, providerData?.website, 'website')}
        </div>

        {/* Languages */}
        <div className={styles.section}>
          <h6 className={styles.sectionTitle}>{languagesLabel}</h6>
          {isEditing ? (
            <div className={styles.languagesContainer}>
              <input
                type="text"
                className={styles.fieldInput}
                value={languagesText}
                onChange={(e) => setLanguagesText(e.target.value)}
                placeholder={enterLanguages}
              />
            </div>
          ) : (
            <div className={styles.tagsContainer}>
              {providerData?.languages?.length > 0 ? (
                providerData.languages.map((lang, index) => (
                  <span key={index} className={styles.tag}>{lang}</span>
                ))
              ) : (
                <span className={styles.fieldValue}>{noLanguages}</span>
              )}
            </div>
          )}
        </div>

        {/* Stats */}
        <div className={styles.statsContainer}>
          <div className={styles.statItem}>
            <div className={styles.statValue}>{getTotalJobsCompleted()}</div>
            <div className={styles.statLabel}>{jobsCompleted}</div>
          </div>
          <div className={styles.statDivider} />
          <div className={styles.statItem}>
            <div className={styles.statValue}>{getResponseTime()}m</div>
            <div className={styles.statLabel}>{avgResponse}</div>
          </div>
          <div className={styles.statDivider} />
          <div className={styles.statItem}>
            <div className={styles.statValue}>{getRating().toFixed(1)}</div>
            <div className={styles.statLabel}>{ratingLabel}</div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className={styles.footerFull}>
        <div className={styles.footerContent}>
          <div className={styles.footerGridFull}>
            <div className={styles.footerBrandColumn}>
              <div className={styles.footerBrandContainer}>
                <div className={styles.footerLogoWrapper}>
                  <img 
                    src="/logo/logo_hsm.png" 
                    alt="Haitian Services"
                    className={styles.footerLogo}
                  />
                </div>
                <div>
                  <div className={styles.footerBrandName}>{homeTitle}</div>
                  <div className={styles.footerBrandDescription}>{connectingHaitians}</div>
                </div>
              </div>
              <div className={styles.footerSocialLinks}>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-facebook.svg" alt="Facebook" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-instagram.svg" alt="Instagram" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-twitter.svg" alt="Twitter" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-youtube.svg" alt="YouTube" width="20" height="20" />
                </a>
              </div>
            </div>

            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{quickLinks}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/providers">{findProviders}</a></li>
                <li><a href="/donation">{supportUs}</a></li>
                <li><a href="/help-support">{helpAndSupport}</a></li>
                <li><a href="/about">{aboutUs}</a></li>
              </ul>
            </div>

            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{forProviders}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/register">{becomeAProvider}</a></li>
                <li><a href="/login">{providerLogin}</a></li>
                <li><a href="/provider-dashboard">{dashboardLabel}</a></li>
                <li><a href="/provider-settings">{settings}</a></li>
              </ul>
            </div>

            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{legal}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/terms">{termsOfService}</a></li>
                <li><a href="/privacy">{privacyPolicy}</a></li>
                <li><a href="/cookies">{cookiePolicy}</a></li>
              </ul>
            </div>
          </div>

          <div className={styles.footerDividerFull}></div>
          <div className={styles.footerBottomFull}>
            <div className={styles.footerFlag}>
              <div className={styles.flagStripes}>
                <div className={`${styles.stripe} ${styles.stripeBlue}`}></div>
                <div className={`${styles.stripe} ${styles.stripeRed}`}></div>
              </div>
            </div>
            <div className={styles.footerBottomText}>
              © 2026 {homeTitle}. {madeWithLove}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ProviderProfilePage;